import { useEffect, useState, useCallback } from 'react';
import { useCurrentOrg, type OrgMembership, type OrgRole } from './useCurrentOrg';

const STORAGE_KEY = 'active-org-id';

/**
 * Active-organization context for instructor flows.
 *
 * Builds on `useCurrentOrg` (which returns memberships) and persists the
 * user's selected org in localStorage. New rows in instructor entities
 * (tests, lists, schedules) should set `org_id = activeOrg?.org_id` so
 * org-aware listings can filter on it. Personal rows keep `org_id = null`
 * for backward compatibility.
 */
export function useActiveOrg() {
  const { memberships, currentOrg, loading, refetch } = useCurrentOrg();
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });

  // Keep the persisted selection valid: if the user lost access, fall back.
  useEffect(() => {
    if (loading) return;
    const valid = memberships.some((m) => m.org_id === activeOrgId);
    if (!valid) {
      const next = currentOrg?.org_id ?? null;
      setActiveOrgIdState(next);
      if (typeof window !== 'undefined') {
        if (next) window.localStorage.setItem(STORAGE_KEY, next);
        else window.localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [loading, memberships, activeOrgId, currentOrg?.org_id]);

  const setActiveOrgId = useCallback((id: string | null) => {
    setActiveOrgIdState(id);
    if (typeof window !== 'undefined') {
      if (id) window.localStorage.setItem(STORAGE_KEY, id);
      else window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const activeOrg: OrgMembership | undefined =
    memberships.find((m) => m.org_id === activeOrgId) ?? currentOrg;

  const role: OrgRole | undefined = activeOrg?.role;
  const canManage =
    !!role && (role === 'owner' || role === 'admin' || role === 'instructor');
  const isOwnerOrAdmin = !!role && (role === 'owner' || role === 'admin');

  return {
    memberships,
    activeOrg,
    activeOrgId: activeOrg?.org_id ?? null,
    role,
    canManage,
    isOwnerOrAdmin,
    setActiveOrgId,
    loading,
    refetch,
  };
}
